import React from 'react';
import { useTranslation } from 'react-i18next';

import { ANIM } from '../../../../theme/tokens';
import type { HomeGauge } from '../../../../mock-data/types';
import { SectionHead } from '../SectionHead';
import { Gauge } from '../Gauge';
import { Gauges } from './OptimizationScore.styles';

// Lumio mark shown beside the section title (source `/lumio-logo.svg`).
const LUMIO_LOGO = '/assets/home/lumio-logo.svg';

export type OptimizationScoreProps = {
  gauges: HomeGauge[];
  onOpenProduct: () => void;
  onOpenAcquisition: () => void;
  onOpenOperations: () => void;
  /** While true, the three gauges show placeholders (the score endpoint is loading). */
  loading?: boolean;
};

/**
 * "Business Optimization Score" — the section header plus the three score gauges
 * (Product, Acquisition, Operations). Each gauge drills into its area root. The
 * three share one endpoint, so they load — then stagger their count-up — together.
 */
export const OptimizationScore = ({
  gauges,
  onOpenProduct,
  onOpenAcquisition,
  onOpenOperations,
  loading = false,
}: OptimizationScoreProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
  <React.Fragment>
    <SectionHead title={t('Business Optimization Score')} sparkSrc={LUMIO_LOGO} first />
    <Gauges>
      {gauges.map((g, index) => (
        <Gauge
          key={g.id}
          id={g.id}
          value={g.value}
          label={g.label}
          status={g.status}
          delay={index * ANIM.HOME_GAUGE_STAGGER_MS}
          loading={loading}
          onOpen={
            g.id === 'product'
              ? onOpenProduct
              : g.id === 'acq'
                ? onOpenAcquisition
                : g.id === 'ops'
                  ? onOpenOperations
                  : undefined
          }
        />
      ))}
    </Gauges>
  </React.Fragment>
  );
};

export default OptimizationScore;
